import { Geolocation } from '@capacitor/geolocation'
import { MapPin } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { createCustomer } from '@/features/customers/api'

export function MobileLeadCapture() {
  const navigate = useNavigate()
  
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [city, setCity] = useState('')
  
  const [lat, setLat] = useState<number | null>(null)
  const [lng, setLng] = useState<number | null>(null)
  
  const [gpsError, setGpsError] = useState<string | null>(null)
  const [submitStatus, setSubmitStatus] = useState<string | null>(null)

  const captureLocation = async () => {
    setGpsError(null)
    try {
      const permission = await Geolocation.requestPermissions()
      if (permission.location === 'denied') {
        setGpsError('Location permission was refused.')
        return
      }
      const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true })
      setLat(position.coords.latitude)
      setLng(position.coords.longitude)
    } catch (error) {
      setGpsError(error instanceof Error ? error.message : 'Could not get a GPS fix.')
    }
  }

  const createLeadMutation = useMutation({
    mutationFn: async () => {
      if (!name) throw new Error('Name is required.')
      return createCustomer({
        // Generate a simple code prefix for leads, backend will accept it
        code: `LEAD-${Date.now().toString().slice(-6)}`,
        name,
        email,
        phone,
        city,
        latitude: lat || undefined,
        longitude: lng || undefined,
        status: 'active',
        tier: 'standard'
      })
    },
    onSuccess: () => {
      setSubmitStatus('Lead captured successfully!')
      setTimeout(() => navigate('/m'), 1500)
    },
    onError: (error) => {
      setSubmitStatus(`Error: ${error instanceof Error ? error.message : 'Failed to capture lead.'}`)
    }
  })

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader 
          title="Capture Lead" 
          description="Register a new prospect from the field." 
        />
        <CardBody className="space-y-4">
          
          {(gpsError || submitStatus?.includes('Error')) && (
            <Alert tone="error">
              {gpsError || submitStatus}
            </Alert>
          )}

          {submitStatus && !submitStatus.includes('Error') && (
            <Alert tone="success">
              {submitStatus}
            </Alert>
          )}

          <div className="space-y-3 rounded-md border border-slate-200 p-4 dark:border-slate-700">
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">Location</h3>
            {lat !== null ? (
              <div className="flex items-center text-sm font-medium text-green-600 dark:text-green-400">
                <MapPin className="mr-2 h-4 w-4" />
                Location captured: {lat.toFixed(4)}, {lng?.toFixed(4)}
              </div>
            ) : (
              <Button 
                variant="secondary" 
                className="w-full" 
                onClick={captureLocation}
              >
                <MapPin className="mr-2 h-4 w-4" aria-hidden="true" />
                Capture Map Pin
              </Button>
            )}
          </div>

          <div className="space-y-3 rounded-md border border-slate-200 p-4 dark:border-slate-700">
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">Lead Details</h3>
            
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:text-white"
                placeholder="Business Name"
              />
            </div>
            
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:text-white"
                placeholder="Phone Number"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:text-white"
                placeholder="Email Address"
              />
            </div>
            
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:text-white"
                placeholder="City"
              />
            </div>
            
            <Button 
              className="w-full" 
              onClick={() => createLeadMutation.mutate()}
              loading={createLeadMutation.isPending}
              disabled={!name}
            >
              Save Lead
            </Button>
          </div>

        </CardBody>
      </Card>
    </div>
  )
}
